/*
 *  Geo Location Popup
 *    Modern Custom Element for Vida Glow Theme
 *
 *  Version:
 *    2.0.0 - 2025/10/09
 */

const popupData = window.GeoPopupData || [];
const GEO_POPUP_REMEMBER_COOKIE = 'GeoPopupRemember';

// Utility function to get cookie value
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Utility function to set cookie
function setCookie(name, value, days = 1) {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
}

class GeoPopup extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.isInitialized = false;
  }

  connectedCallback() {
    if (!this.isInitialized) {
      this.init();
      this.isInitialized = true;
    }
  }

  async init() {
    // Check if popup is enabled
    if (!this.hasAttribute('data-enabled') || this.getAttribute('data-enabled') !== 'true') {
      return;
    }

    // Get modal reference
    this.modal = this.querySelector('x-modal');
    if (!this.modal) {
      console.warn('GeoPopup: x-modal element not found');
      return;
    }

    // Set up close button listeners
    this.setupCloseListeners();

    // Initialize country detection
    await this.detectAndSetCountry();
  }

  setupCloseListeners() {
    const closeButtons = this.querySelectorAll('[data-popup-close]');
    closeButtons.forEach(button => {
      button.addEventListener('click', (event) => this.handleClose(event));
    });
  }

  async detectAndSetCountry() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}browsing_context_suggestions.json`);
      const data = await response.json();
      
      const countryCode = data.detected_values.country.handle;
      const countryName = data.detected_values.country.name;
      
      // Find matching country data
      const countryData = this.findCountryData(countryCode);
      
      if (!countryData) {
        return; // No matching country configuration
      }

      // Check if we're already on the correct domain
      if (countryData.domain === window.location.origin) {
        return;
      }

      // Check if user has already dismissed this popup
      const remember = getCookie(GEO_POPUP_REMEMBER_COOKIE);
      if (remember === countryData.domain) {
        return;
      }

      // Update popup content
      this.updatePopupContent(countryName, countryCode, countryData);
      
      // Show popup with delay
      this.showPopup();
      
    } catch (error) {
      console.error('GeoPopup: Error fetching geolocation data:', error);
    }
  }

  findCountryData(countryCode) {
    for (const data of popupData) {
      const countryCodeArray = data.code.replace(/\s/g, '').split(',');
      
      if (countryCodeArray.includes(countryCode)) {
        return data;
      }
    }
    return null;
  }

  updatePopupContent(countryName, countryCode, countryData) {
    // Update title
    const titleElement = this.querySelector('[data-geo-title]');
    if (titleElement && window.GeoPopupTitle) {
      let title = window.GeoPopupTitle.replace(/%country%/gi, countryName);
      title = title.replace(/\n/g, '<br>');
      titleElement.innerHTML = title;
    }

    // Update content
    const contentElement = this.querySelector('[data-geo-content]');
    if (contentElement && window.GeoPopupMessage) {
      let content = window.GeoPopupMessage.replace(/%country%/gi, countryName);
      content = content.replace(/\n/g, '<br>');
      contentElement.innerHTML = content;
    }

    // Update go button
    const goButton = this.querySelector('[data-geo-go]');
    if (goButton && window.GeoPopupButton) {
      const buttonText = window.GeoPopupButton.replace(/%country%/gi, countryCode);
      goButton.innerHTML = buttonText;
      goButton.setAttribute('href', countryData.domain);
    }

    // Store country data for later use
    this.currentCountryData = countryData;
  }

  showPopup() {
    const remember = getCookie(GEO_POPUP_REMEMBER_COOKIE);
    if (remember) return;

    const delay = parseInt(this.getAttribute('data-geo-delay') || '1') * 1000;
    
    setTimeout(() => {
      if (this.modal && typeof this.modal.show === 'function') {
        this.modal.show();
      } else {
        // Fallback for older modal implementation
        this.modal?.setAttribute('open', '');
      }
    }, delay);
  }

  closePopup() {
    if (this.modal && typeof this.modal.hide === 'function') {
      this.modal.hide();
    } else {
      // Fallback for older modal implementation
      this.modal?.removeAttribute('open');
    }
  }

  handleClose(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const expireDays = parseInt(this.getAttribute('data-geo-expire') || '1');
    
    this.closePopup();
    this.rememberChoice(expireDays);
  }

  rememberChoice(days) {
    if (this.currentCountryData) {
      setCookie(GEO_POPUP_REMEMBER_COOKIE, this.currentCountryData.domain, days);
    }
  }
}

// Register the custom element
if (!window.customElements.get('geo-popup')) {
  window.customElements.define('geo-popup', GeoPopup);
}
